<template>
  <div class="storage-question-edit">
    <div v-if="loading" class="text-center py-3">
      <b-spinner />
    </div>

    <b-alert :show="Boolean(loadError)" variant="danger" class="p-1">{{ loadError }}</b-alert>

    <template v-if="question">
      <!-- Шапка: пакет · тема · цена · даты · сыгран -->
      <b-card no-body class="mb-1">
        <b-card-body class="py-1">
          <div class="d-flex align-items-center flex-wrap">
            <h4 class="mb-0 mr-1">Вопрос #{{ question.id }}</h4>
            <span v-if="question.game" class="mr-1">
              <b-link :to="{ name: 'question-storage-pack', params: { id: question.game.id } }">{{ question.game.name }}</b-link>
              <template v-if="question.theme"> · {{ question.theme }}</template>
              <template v-if="question.price !== null"> · {{ question.price }}</template>
            </span>
            <span v-else class="mr-1 text-muted">Не входит в пакет</span>
            <span class="mr-1">Премьера: {{ formatDate(question.premiere_at) }}</span>
            <span class="mr-1">Последний отыгрыш: {{ formatDate(question.last_played_at) }}</span>
            <b-badge :variant="question.is_played ? 'light-success' : 'light-secondary'" class="mr-50">
              {{ question.is_played ? 'сыгран' : 'не сыгран' }}
            </b-badge>
            <b-badge v-if="specialTypeLabel" variant="light-info" class="mr-50">{{ specialTypeLabel }}</b-badge>
            <b-badge v-if="question.is_for_all" variant="light-info" class="mr-50">для всех</b-badge>
          </div>
        </b-card-body>
      </b-card>

      <b-card class="mb-1">
        <b-row>
          <b-col md="3" class="mb-1">
            <label class="small mb-25">Тип</label>
            <b-form-select v-model="form.kind" :options="kindOptions" :disabled="!canManage" />
          </b-col>
          <b-col md="5" class="mb-1">
            <label class="small mb-25">Механика</label>
            <b-form-select
              :value="form.mechanic_id"
              :options="mechanicSelectOptions"
              :disabled="!canManage"
              @change="onMechanicChange"
            />
          </b-col>
        </b-row>

        <b-alert :show="mechanicWarning" variant="warning" class="p-1 mb-1">
          Привязки из слотов, которых нет у новой механики, переедут в «Ответ»<template v-if="droppedSlotValues.length">;
            значения текстовых слотов «{{ droppedSlotValues.join('», «') }}» будут удалены</template>.
          Изменения применятся после «Сохранить».
        </b-alert>

        <div v-if="question.question" class="mb-1">
          <label class="small mb-25">Текст вопроса</label>
          <div class="question-text">{{ question.question }}</div>
        </div>

        <b-row>
          <b-col md="6" class="mb-1">
            <label class="small mb-25">Ответ</label>
            <b-form-textarea
              v-model="form.answer"
              rows="2"
              max-rows="6"
              placeholder="Текст ответа"
              :disabled="!canManage"
            />
          </b-col>
          <b-col md="6" class="mb-1">
            <label class="small mb-25">Комментарий к ответу</label>
            <b-form-textarea
              v-model="form.answer_comment"
              rows="2"
              max-rows="6"
              placeholder="Комментарий"
              :disabled="!canManage"
            />
          </b-col>
        </b-row>
      </b-card>

      <!-- Части: правка текста, удаление и порядок сохраняются сразу -->
      <b-card class="mb-1">
        <div class="d-flex align-items-center mb-1">
          <h5 class="mb-0">Части</h5>
          <small class="text-muted ml-1">правка текста, удаление и перестановка сохраняются сразу</small>
        </div>
        <b-row>
          <b-col
            v-for="side in sides"
            :key="side.value"
            md="6"
            class="mb-1"
          >
            <div class="text-muted small text-uppercase mb-50">{{ side.label }}</div>
            <draggable
              :list="partsBySide[side.value]"
              :disabled="!canManage || isPartsBusy"
              handle=".part-handle"
              tag="div"
              @start="rememberOrder"
              @end="saveOrder"
            >
              <div
                v-for="part in partsBySide[side.value]"
                :key="part.id"
                class="d-flex align-items-start border rounded p-50 mb-50"
              >
                <feather-icon
                  v-if="canManage"
                  icon="MenuIcon"
                  class="part-handle cursor-move flex-shrink-0 text-muted mr-50 mt-25"
                />
                <div class="flex-grow-1 min-width-0">
                  <template v-if="part.type === 'text'">
                    <b-form-textarea
                      v-model="partTexts[part.id]"
                      rows="1"
                      max-rows="6"
                      size="sm"
                      :disabled="!canManage || isPartsBusy"
                    />
                    <div v-if="isPartTextChanged(part)" class="mt-25">
                      <b-button variant="primary" size="sm" class="mr-50" :disabled="isPartsBusy" @click="savePartText(part)">Сохранить текст</b-button>
                      <b-button variant="flat-secondary" size="sm" @click="resetPartText(part)">Отмена</b-button>
                    </div>
                  </template>
                  <question-parts-preview v-else :parts="[previewPart(part)]" />
                </div>
                <b-badge v-if="part.is_hint" variant="light-warning" class="ml-50 flex-shrink-0">подсказка</b-badge>
                <b-button
                  v-if="canManage"
                  variant="flat-danger"
                  size="sm"
                  class="btn-icon ml-50 flex-shrink-0"
                  title="Удалить часть"
                  :disabled="isPartsBusy"
                  @click="confirmDeletePart(part)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </div>
            </draggable>
            <div v-if="!partsBySide[side.value].length" class="text-muted small mb-50">—</div>

            <div v-if="canManage" class="d-flex align-items-start">
              <b-form-textarea
                v-model="newPartTexts[side.value]"
                rows="1"
                max-rows="4"
                size="sm"
                class="mr-50"
                placeholder="Новый текст"
              />
              <b-button
                variant="outline-primary"
                size="sm"
                class="text-nowrap"
                :disabled="isPartsBusy || !String(newPartTexts[side.value] || '').trim()"
                @click="addTextPart(side.value)"
              >
                Добавить текст
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <b-card class="mb-1" title="Привязки">
        <question-bindings-editor
          :slots="displaySlots"
          :value="bindingsValue"
          :unresolved="[]"
          :lookups="lookups"
          :readonly="!canManage"
          :context-key="question ? question.id : null"
          @input="onBindingsInput"
        />
      </b-card>

      <div class="d-flex align-items-center mb-2">
        <b-button
          v-if="canManage"
          variant="primary"
          class="mr-1"
          :disabled="isSaving"
          @click="save"
        >
          <b-spinner v-if="isSaving" small class="mr-50" />
          Сохранить
        </b-button>
        <b-button variant="flat-secondary" @click="goBack">К списку</b-button>
        <small v-if="!canManage" class="text-muted ml-1">Правка доступна только с правом на изменение вопросов</small>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCard, BCardBody, BCol, BFormSelect, BFormTextarea, BLink, BRow, BSpinner,
} from 'bootstrap-vue'
import draggable from 'vuedraggable'
import {
  computed, getCurrentInstance, ref, watch,
} from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import questionStorageStoreModule from './questionStorageStoreModule'
import QuestionPartsPreview from './components/QuestionPartsPreview.vue'
import QuestionBindingsEditor from './components/QuestionBindingsEditor.vue'
import { toSelectGroups } from './mechanicGroups'

const MODULE = 'question-storage'
// Слот по умолчанию — как MechanicSlots::DEFAULT_SLOT на бэкенде
const DEFAULT_SLOT_CODE = 'answer'
const SIDE_QUESTION = 'question'
const SIDE_ANSWER = 'answer'

// PHP отдаёт пустой ассоциативный массив как [] — приводим к объекту
const asObject = value => (value && !Array.isArray(value) && typeof value === 'object' ? value : {})

const pluralize = (count, one, few, many) => {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few

  return many
}

const isUnresolvedItem = item => item.id === null || item.id === undefined

const formatDate = value => {
  if (!value) return '—'
  const [year, month, day] = String(value).slice(0, 10).split('-')

  return `${day}.${month}.${year}`
}

// 422 бэкенда: первое сообщение из errors, иначе message
const errorMessage = (error, fallback) => {
  const data = error && error.response && error.response.data
  if (data && data.errors) {
    const first = Object.values(data.errors)[0]
    if (Array.isArray(first) && first.length) return first[0]
  }

  return (data && data.message) || fallback
}

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormSelect,
    BFormTextarea,
    BLink,
    BRow,
    BSpinner,
    draggable,
    QuestionPartsPreview,
    QuestionBindingsEditor,
  },
  setup(props, { root }) {
    // Модуль стора не снимаем: Vue 2 создаёт компонент следующего роута до уничтожения текущего (см. import/PackImport.vue)
    if (!store.hasModule(MODULE)) store.registerModule(MODULE, questionStorageStoreModule)

    const toast = useToast()
    const instance = getCurrentInstance().proxy
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })
    const showSuccess = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'CheckIcon', variant: 'success' },
    })

    const canManage = computed(() => instance.$can('manage', 'questions'))

    const kindOptions = [
      { value: 'svoya_igra', text: 'Своя игра' },
      { value: 'quiz', text: 'Квиз' },
    ]
    const sides = [
      { value: SIDE_QUESTION, label: 'Вопрос' },
      { value: SIDE_ANSWER, label: 'Ответ' },
    ]
    const specialTypeLabels = { auction: 'аукцион', cat: 'кот в мешке' }

    const question = ref(null)
    const loading = ref(false)
    const loadError = ref('')
    const lookups = ref({})
    const isSaving = ref(false)
    const isPartsBusy = ref(false)

    // Редактируемые поля (тело PUT question-storage/questions/{id})
    const form = ref({
      kind: 'svoya_igra', mechanic_id: null, answer: '', answer_comment: '',
    })
    const bindingsValue = ref({
      bindings: {}, decades: [], cluster_ids: [], origin: null, slot_values: {},
    })
    const mechanicWarning = ref(false)
    const droppedSlotValues = ref([])
    // Снимок сохранённых привязок: ключ «сущность|id» → исходные слоты; смена механики считается от него, а не стопкой
    let snapshotSlotsByKey = {}
    // Все введённые значения текстовых слотов — возврат к механике возвращает их
    let slotValuesMemory = {}

    // Части: по сторонам, в порядке order; тексты текстовых частей — отдельно, чтобы видеть несохранённые
    const partsBySide = ref({ [SIDE_QUESTION]: [], [SIDE_ANSWER]: [] })
    const partTexts = ref({})
    const newPartTexts = ref({ [SIDE_QUESTION]: '', [SIDE_ANSWER]: '' })

    store.dispatch(`${MODULE}/fetchLookups`)
      .then(response => { lookups.value = response.data.data || {} })
      .catch(() => showError('Не удалось загрузить справочники'))

    const mechanics = computed(() => lookups.value.mechanics || [])
    const mechanicsById = computed(() => {
      const result = {}
      mechanics.value.forEach(mechanic => { result[mechanic.id] = mechanic })

      return result
    })

    // Несохранённые правки текста частей (буфер отличается от текста части) переживают обновление списка
    const setParts = parts => {
      const dirtyTexts = {}
      const previousParts = [...partsBySide.value[SIDE_QUESTION], ...partsBySide.value[SIDE_ANSWER]]
      previousParts.forEach(part => {
        const buffer = partTexts.value[part.id]
        if (part.type === 'text' && buffer !== undefined && buffer !== (part.text || '')) dirtyTexts[part.id] = buffer
      })

      const sorted = [...(parts || [])].sort((first, second) => (first.order || 0) - (second.order || 0))
      partsBySide.value = {
        [SIDE_QUESTION]: sorted.filter(part => part.side !== SIDE_ANSWER),
        [SIDE_ANSWER]: sorted.filter(part => part.side === SIDE_ANSWER),
      }
      const texts = {}
      sorted.forEach(part => {
        if (part.type !== 'text') return
        texts[part.id] = dirtyTexts[part.id] !== undefined ? dirtyTexts[part.id] : (part.text || '')
      })
      partTexts.value = texts
    }

    const applyCard = card => {
      const bindingsData = card.bindings || {}
      question.value = { ...card, slots: card.slots || [] }
      form.value = {
        kind: card.kind || 'svoya_igra',
        mechanic_id: card.mechanic_id === undefined ? null : card.mechanic_id,
        answer: card.answer || '',
        answer_comment: card.answer_comment || '',
      }
      bindingsValue.value = {
        bindings: asObject(bindingsData.bindings),
        decades: bindingsData.decades || [],
        cluster_ids: bindingsData.cluster_ids || [],
        origin: bindingsData.origin || null,
        slot_values: asObject(bindingsData.slot_values),
      }
      mechanicWarning.value = false
      droppedSlotValues.value = []
      snapshotSlotsByKey = {}
      Object.entries(bindingsValue.value.bindings).forEach(([code, items]) => (items || []).forEach(item => {
        if (isUnresolvedItem(item)) return
        const key = `${item.entity}|${item.id}`
        snapshotSlotsByKey[key] = [...(snapshotSlotsByKey[key] || []), code]
      }))
      slotValuesMemory = { ...bindingsValue.value.slot_values }
      setParts(card.parts)
    }

    const load = () => {
      loading.value = true
      loadError.value = ''
      question.value = null
      store.dispatch(`${MODULE}/fetchQuestion`, root.$route.params.id)
        .then(response => applyCard(response.data.data))
        .catch(error => {
          loadError.value = error && error.response && error.response.status === 404
            ? 'Вопрос не найден'
            : 'Не удалось загрузить вопрос'
        })
        .finally(() => { loading.value = false })
    }
    load()
    watch(() => root.$route.params.id, (id, previousId) => {
      if (root.$route.name === 'question-storage-edit' && id !== previousId) load()
    })

    const specialTypeLabel = computed(() => (question.value && question.value.special_type
      ? specialTypeLabels[question.value.special_type] || question.value.special_type
      : ''))

    // --- Механика и слоты ---
    const mechanicSelectOptions = computed(() => [
      { value: null, text: '— без механики —' },
      ...toSelectGroups(
        mechanics.value.filter(mechanic => !mechanic.is_archived || mechanic.id === form.value.mechanic_id
          || (question.value && mechanic.id === question.value.mechanic_id)),
        mechanic => ({ value: mechanic.id, text: mechanic.is_archived ? `${mechanic.name} (архив)` : mechanic.name }),
      ),
    ])

    // Вид контента вопроса без механики — как QuestionStorageQuery::contentKind: фильмы без артистов → кино
    const defaultSlots = () => {
      const items = Object.values(bindingsValue.value.bindings).flat()
      const hasMovies = items.some(item => item.entity === 'movie')
      const hasArtists = items.some(item => item.entity === 'artist')

      return [{
        code: DEFAULT_SLOT_CODE,
        name: 'Ответ',
        entity: hasMovies && !hasArtists ? 'movie' : 'artist',
        multiple: true,
        required: false,
        counts_as_played: true,
      }]
    }

    // Слоты выбранной механики: до смены — как отдал бэкенд, после — из справочника (там уже MechanicSlots::slotsFor)
    const mechanicSlots = mechanicId => {
      if (question.value && mechanicId === question.value.mechanic_id && question.value.slots.length) return question.value.slots
      const mechanic = mechanicsById.value[mechanicId]
      if (mechanic && Array.isArray(mechanic.slots) && mechanic.slots.length) return mechanic.slots

      return defaultSlots()
    }

    // Привязки в слотах вне механики тоже показываем, чтобы их было видно и можно убрать
    const displaySlots = computed(() => {
      const slots = [...mechanicSlots(form.value.mechanic_id)]
      const codes = new Set(slots.map(slot => slot.code))
      Object.entries(bindingsValue.value.bindings).forEach(([code, items]) => {
        if (codes.has(code) || !items || !items.length) return
        slots.push({
          code,
          name: code === DEFAULT_SLOT_CODE ? 'Ответ' : code,
          entity: items[0].entity,
          multiple: true,
          required: false,
          counts_as_played: true,
        })
        codes.add(code)
      })

      return slots
    })

    // Как QuestionBindingsService::remapForMechanic: чужие слоты → «answer», текстовые значения чужих слотов отбрасываются.
    // Считаем от снимка сохранённого вопроса: привязка из снимка идёт в свой исходный слот, если он есть у механики;
    // добавленная вручную остаётся в своём слоте, если он есть, иначе — в «answer»
    const onMechanicChange = mechanicId => {
      const nextMechanicId = mechanicId === undefined ? null : mechanicId
      if (nextMechanicId === form.value.mechanic_id) return
      form.value.mechanic_id = nextMechanicId

      const allowedCodes = new Set(mechanicSlots(nextMechanicId).map(slot => slot.code))
      const availableSnapshotSlots = {}
      Object.entries(snapshotSlotsByKey).forEach(([key, codes]) => { availableSnapshotSlots[key] = [...codes] })

      const remapped = {}
      let moved = false
      Object.entries(bindingsValue.value.bindings).forEach(([code, items]) => (items || []).forEach(item => {
        const snapshotCodes = isUnresolvedItem(item) ? null : availableSnapshotSlots[`${item.entity}|${item.id}`]
        const originCode = snapshotCodes && snapshotCodes.length ? snapshotCodes.shift() : code
        const targetCode = allowedCodes.has(originCode) ? originCode : DEFAULT_SLOT_CODE
        if (targetCode !== originCode) moved = true
        remapped[targetCode] = [...(remapped[targetCode] || []), item]
      }))

      slotValuesMemory = { ...slotValuesMemory, ...bindingsValue.value.slot_values }
      const keptValues = {}
      const dropped = []
      Object.entries(slotValuesMemory).forEach(([code, text]) => {
        if (allowedCodes.has(code)) {
          keptValues[code] = text
        } else if (String(text || '').trim() !== '') {
          dropped.push(code)
        }
      })

      bindingsValue.value = { ...bindingsValue.value, bindings: remapped, slot_values: keptValues }
      // Вернулись к сохранённой механике — бэкенд ничего не перенесёт
      const isSavedMechanic = Boolean(question.value) && nextMechanicId === question.value.mechanic_id
      mechanicWarning.value = !isSavedMechanic && (moved || dropped.length > 0)
      droppedSlotValues.value = isSavedMechanic ? [] : dropped
    }

    const onBindingsInput = value => {
      bindingsValue.value = {
        bindings: asObject(value.bindings),
        decades: value.decades || [],
        cluster_ids: value.cluster_ids || [],
        origin: value.origin || null,
        slot_values: asObject(value.slot_values),
      }
    }

    // --- Сохранение ---
    const unresolvedCount = computed(() => Object.values(bindingsValue.value.bindings)
      .reduce((count, items) => count + (items || []).filter(item => item.entity !== 'text' && isUnresolvedItem(item)).length, 0))

    const buildPayload = () => {
      const bindings = {}
      Object.entries(bindingsValue.value.bindings).forEach(([code, items]) => {
        const resolved = (items || []).filter(item => !isUnresolvedItem(item)).map(item => ({ entity: item.entity, id: item.id }))
        if (resolved.length) bindings[code] = resolved
      })

      return {
        kind: form.value.kind,
        mechanic_id: form.value.mechanic_id,
        answer: form.value.answer,
        answer_comment: String(form.value.answer_comment || '').trim() === '' ? null : form.value.answer_comment,
        bindings,
        decades: bindingsValue.value.decades,
        cluster_ids: bindingsValue.value.cluster_ids,
        origin: bindingsValue.value.origin,
        slot_values: bindingsValue.value.slot_values,
      }
    }

    const save = async () => {
      if (!canManage.value || isSaving.value) return
      // Бэкенд молча отбрасывает привязки без id — предупреждаем заранее
      if (unresolvedCount.value) {
        const confirmed = await root.$bvModal.msgBoxConfirm(
          `${unresolvedCount.value} ${pluralize(unresolvedCount.value, 'привязка', 'привязки', 'привязок')} без найденной в базе сущности (красные чипы) не сохранятся. Продолжить?`,
          {
            title: 'Непривязанные сущности',
            okTitle: 'Сохранить',
            cancelTitle: 'Отмена',
            okVariant: 'warning',
            centered: true,
          },
        )
        if (!confirmed) return
      }

      isSaving.value = true
      store.dispatch(`${MODULE}/updateQuestion`, { id: question.value.id, data: buildPayload() })
        .then(response => {
          applyCard(response.data.data)
          showSuccess('Вопрос сохранён')
        })
        .catch(error => showError(errorMessage(error, 'Не удалось сохранить вопрос')))
        .finally(() => { isSaving.value = false })
    }

    // --- Части ---
    const allParts = () => [...partsBySide.value[SIDE_QUESTION], ...partsBySide.value[SIDE_ANSWER]]

    // Медиа без файла показываем бейджем «нет файла»
    const previewPart = part => ({ ...part, missing: part.type !== 'text' && !part.url })

    const isPartTextChanged = part => (partTexts.value[part.id] || '') !== (part.text || '')
    const resetPartText = part => { partTexts.value = { ...partTexts.value, [part.id]: part.text || '' } }

    const savePartText = part => {
      const text = partTexts.value[part.id] || ''
      if (!text.trim()) {
        showError('Текст части не может быть пустым — удалите часть')
        return
      }
      isPartsBusy.value = true
      store.dispatch(`${MODULE}/updatePart`, { questionId: question.value.id, partId: part.id, data: { text } })
        .then(response => {
          Object.assign(part, response.data.data)
          resetPartText(part)
          showSuccess('Текст части сохранён')
        })
        .catch(error => showError(errorMessage(error, 'Не удалось сохранить текст части')))
        .finally(() => { isPartsBusy.value = false })
    }

    const confirmDeletePart = async part => {
      const confirmed = await root.$bvModal.msgBoxConfirm('Часть будет удалена из вопроса сразу. Продолжить?', {
        title: 'Удаление части',
        okTitle: 'Удалить',
        cancelTitle: 'Отмена',
        okVariant: 'danger',
        centered: true,
      })
      if (!confirmed) return
      isPartsBusy.value = true
      store.dispatch(`${MODULE}/deletePart`, { questionId: question.value.id, partId: part.id })
        .then(() => {
          setParts(allParts().filter(candidate => candidate.id !== part.id))
          showSuccess('Часть удалена')
        })
        .catch(error => showError(errorMessage(error, 'Не удалось удалить часть')))
        .finally(() => { isPartsBusy.value = false })
    }

    // Порядок общий на вопрос: сначала части вопроса, затем части ответа; бэкенд требует все id частей
    let orderBeforeDrag = ''
    const orderKey = () => allParts().map(part => part.id).join(',')
    const rememberOrder = () => { orderBeforeDrag = orderKey() }

    // После ошибки перечитываем только части — несохранённые правки карточки не трогаем
    const reloadParts = () => store.dispatch(`${MODULE}/fetchQuestion`, question.value.id)
      .then(response => setParts(response.data.data.parts))
      .catch(() => showError('Не удалось перечитать части'))

    const saveOrder = () => {
      const parts = allParts()
      if (!parts.length || orderKey() === orderBeforeDrag) return
      isPartsBusy.value = true
      store.dispatch(`${MODULE}/reorderParts`, { questionId: question.value.id, partIds: parts.map(part => part.id) })
        .then(response => setParts(response.data.data))
        .catch(error => {
          showError(errorMessage(error, 'Не удалось сохранить порядок частей'))

          return reloadParts()
        })
        .finally(() => { isPartsBusy.value = false })
    }

    const addTextPart = side => {
      const text = String(newPartTexts.value[side] || '').trim()
      if (!text) return
      isPartsBusy.value = true
      store.dispatch(`${MODULE}/addTextPart`, { questionId: question.value.id, data: { text, side, is_hint: false } })
        .then(response => {
          setParts([...allParts(), response.data.data])
          newPartTexts.value = { ...newPartTexts.value, [side]: '' }
          showSuccess('Текстовая часть добавлена')
        })
        .catch(error => showError(errorMessage(error, 'Не удалось добавить часть')))
        .finally(() => { isPartsBusy.value = false })
    }

    const goBack = () => {
      if (window.history.length > 1) {
        root.$router.back()
      } else {
        root.$router.push({ name: 'question-storage' })
      }
    }

    return {
      canManage,
      kindOptions,
      sides,
      question,
      loading,
      loadError,
      lookups,
      isSaving,
      isPartsBusy,
      form,
      bindingsValue,
      mechanicWarning,
      droppedSlotValues,
      partsBySide,
      partTexts,
      newPartTexts,
      specialTypeLabel,
      mechanicSelectOptions,
      displaySlots,
      onMechanicChange,
      onBindingsInput,
      save,
      previewPart,
      isPartTextChanged,
      resetPartText,
      savePartText,
      confirmDeletePart,
      rememberOrder,
      saveOrder,
      addTextPart,
      goBack,
      formatDate,
    }
  },
}
</script>

<style scoped>
.question-text {
  white-space: pre-line;
}
.min-width-0 {
  min-width: 0;
}
</style>
